import React, { useContext, useMemo } from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import styled from 'styled-components';

import { convertDtVal } from 'src/presentation/components/pages/sidekick/threads/utils/utils';
import DatePicker from 'src/presentation/components/forms/DatePicker';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import { SelectOption } from '../Rule/helpers/memoriesHelper';
import InputField, { InputType } from 'src/presentation/components/forms/InputField';
import { FieldContained } from '@ad-scripts/presentation/components/common/input_field';
import FormContext from 'src/presentation/contexts/FormContext';

const times = ['Seconds', 'Minutes', 'Hours', 'Days', 'Business days', 'Weeks', 'Months', 'Years'];

const opts: SelectOption[] = [
  { label: 'Ago', value: 'Ago' },
  { label: 'From now', value: 'From now' },
];

enum DateSelectorType {
  RelativeDate = 'Relative date',
  ExactDate = 'Exact date',
}

const dateSelectorType: SelectOption[] = [
  { label: DateSelectorType.RelativeDate, value: DateSelectorType.RelativeDate },
  { label: DateSelectorType.ExactDate, value: DateSelectorType.ExactDate },
];

const TimestampControl = ({ attr, prompt, description, disabled }) => {
  const { workingState, onBlur } = useContext(FormContext);

  const value = useMemo(() => {
    const value = get(workingState, attr) as string[];

    if (!value) {
      return [0, 'Days', 'Ago'];
    }

    return value;
  }, [attr, workingState]) as [number, string, string];

  const timeOpts: SelectOption[] = times.map(
    (v: string): SelectOption => ({
      label: v,
      value: v,
    })
  );

  const [tsValue, setTsValue] = React.useState<[number, string, string]>(value);

  const handleChange = (val: string | number, index: number) => {
    tsValue[index] = val as string | number;
    setTsValue([...tsValue]);

    if (index > 0) {
      handleBlur();
    }
  };

  const handleBlur = () => {
    const dt = convertDtVal(tsValue as string[]);
    const newVal = set({}, attr, dt);

    onBlur && onBlur({ currentState: newVal });
  };

  const isDateControl = !Array.isArray(tsValue);

  const handleChangeDatePicker = (val: string) => {
    const newVal = set({}, attr, val);
    onBlur && onBlur({ currentState: newVal });
  };

  const [dateSelType, setDateSelType] = React.useState(DateSelectorType.RelativeDate);
  const handleDateSelectorType = (val: DateSelectorType) => {
    setDateSelType(val);
  };

  return (
    <FieldContained
      label={prompt}
      description={description}
      control={
        <StyledTimestampControlContainer>
          {!isDateControl && (
            <StyledDateTypeDropdown
              className="dropdown"
              disabled={disabled}
              fitOpenerWidth={false}
              onChange={handleDateSelectorType}
              options={dateSelectorType}
              value={dateSelType}
            />
          )}
          <StyledTimestampControl>
            {isDateControl || dateSelType === DateSelectorType.ExactDate ? (
              <DatePicker disabled={disabled} attr={attr} onChange={handleChangeDatePicker} />
            ) : (
              <>
                <StyledInputField
                  className="input-number"
                  disabled={disabled}
                  onChange={val => handleChange(val, 0)}
                  type={InputType.number}
                  value={tsValue[0]}
                  onBlur={handleBlur}
                />
                <StyledDropdown
                  className="dropdown"
                  disabled={disabled}
                  fitOpenerWidth={false}
                  onChange={val => handleChange(val, 1)}
                  options={timeOpts}
                  value={tsValue[1]}
                />
                <StyledDropdown
                  className="dropdown"
                  disabled={disabled}
                  fitOpenerWidth={false}
                  onChange={val => handleChange(val, 2)}
                  options={opts}
                  value={tsValue[2]}
                />
              </>
            )}
          </StyledTimestampControl>
        </StyledTimestampControlContainer>
      }
    />
  );
};

const StyledTimestampControlContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledDateTypeDropdown = styled(Dropdown)`
  width: 140px;
  margin-bottom: 0;
  button {
    width: 100%;
  }
`;

const StyledDropdown = styled(Dropdown)`
  min-width: 90px;
  button {
    width: 100%;
  }
`;

const StyledInputField = styled(InputField)`
  width: 100px;
  input {
    width: 100%;
  }
`;

const StyledTimestampControl = styled.div`
  display: flex;
  gap: 8px;
`;

export default TimestampControl;
